/**
 * At most one import run at a time, decided synchronously.
 *
 * The screen's Import button cannot rely on its own re-render to disappear: a second tap can land
 * before React commits the `IMPORTING` step, and every manufacture of the run would then be
 * written twice (stock, cost and dailyStats moved twice, in both stores). The flag is checked and
 * set in the same synchronous call, before the first await, and cleared when the run settles —
 * resolved or rejected.
 */

export interface RunGuard {
  /**
   * Runs `task` unless a run is in flight. Returns `{ ran: false }` without calling it when one
   * is; otherwise the task's value. A rejection is passed through, and frees the guard.
   */
  runExclusive: <T>(task: () => Promise<T>) => Promise<{ ran: true; value: T } | { ran: false }>
  isRunning: () => boolean
}

export const createRunGuard = (): RunGuard => {
  let running = false
  return {
    runExclusive: async <T>(task: () => Promise<T>) => {
      if (running) return { ran: false as const }
      running = true
      try {
        return { ran: true as const, value: await task() }
      } finally {
        running = false
      }
    },
    isRunning: () => running,
  }
}
